/* eslint-disable no-underscore-dangle */
import AccountSet from '@/types/comparison-account-set';
import ComparisonItem from '@/types/comparison-item';

/**
 * Remaps account set indices from comparison items to proposal items.
 * This is necessary when the proposal items have been reordered or filtered compared to the original comparison items.
 *
 * @param accountSets - The original account sets with indices based on comparison items
 * @param comparisonItems - The list of items in the original comparison
 * @param proposalItems - The list of items in the proposal, which may be reordered/filtered
 * @returns A new array of account sets with indices remapped to the proposal items
 */

export const remapAccountSetIndices = (
  accountSets: AccountSet[] | undefined,
  comparisonItems: ComparisonItem[],
  proposalItems: ComparisonItem[],
): AccountSet[] | undefined => {
  if (!accountSets || accountSets.length === 0) {
    return accountSets;
  }

  const proposalIndexById = new Map<string, number>();
  proposalItems.forEach((item, index) => {
    if (item?._id) {
      proposalIndexById.set(item._id, index);
    }
  });

  // Remap comparison-based indices to proposal-based indices using item ids.
  return accountSets.map(accountSet => {
    const currentComparisonItem = comparisonItems[accountSet.current];
    const currentProposalIndex = proposalIndexById.get(currentComparisonItem?._id) ?? -1;

    const remappedRecommendations = accountSet.recommendations
      .map(recommendation => {
        const comparisonItem = comparisonItems[recommendation.itemId];
        const proposalIndex = comparisonItem ? proposalIndexById.get(comparisonItem._id) : undefined;

        if (proposalIndex === undefined) {
          return undefined;
        }

        return {
          ...recommendation,
          itemId: proposalIndex,
        };
      })
      .filter(recommendation => recommendation !== undefined);

    return {
      ...accountSet,
      current: currentProposalIndex,
      recommendations: remappedRecommendations,
    };
  });
};
